import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { observer } from "mobx-react-lite";

import { classNames } from "primereact/utils";

import { faHome } from "@fortawesome/pro-regular-svg-icons";

import { translationKeys } from "../../../../models/app-translation-keys";
import { RouteConstants } from "../../../../models/enums";
import { useStore } from "../../../../stores/StoreContext";
import { ActionButton } from "../../../Shared/Buttons";
import { CompanyLogo, FeatureList, SidebarToggleButton } from "../../../Shared/Components";
import { SidebarFeatureAreaItem } from "../SidebarFeatureAreaItem/SidebarFeatureAreaItem";

import "./ContentSidebar.scss";

const RawContentSidebar: FunctionComponent = () => {
  const { t } = useTranslation();
  const { navigationStore, sidebarStore } = useStore();

  const [isDelayedHidden, setIsDelayedHidden] = useState(true);

  useEffect(() => {
    sidebarStore.setOverlaySidebarVisible(false);
  }, [sidebarStore]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (sidebarStore.isCollapsed) {
      timeoutId = setTimeout(() => setIsDelayedHidden(true), 200);
    } else {
      setIsDelayedHidden(false);
    }
    return () => clearTimeout(timeoutId);
  }, [sidebarStore.isCollapsed]);

  return (
    <div
      className={classNames("sidebar-content-wrapper", {
        "is-collapsed": sidebarStore.isCollapsed,
      })}
    >
      <div className="sidebar-content-container">
        <div className="sidebar-header">
          {!isDelayedHidden && (
            <div className="company-logo">
              <CompanyLogo />
            </div>
          )}

          <div className="toggle-sidebar-button">
            <SidebarToggleButton onClick={() => sidebarStore.setIsCollapsed(!sidebarStore.isCollapsed)} />
          </div>
        </div>

        <div className="sidebar-home-navigation">
          <Link to={RouteConstants.LandingPage} style={{ width: "100%" }}>
            <ActionButton
              id="back-to-landing-page-button"
              label={sidebarStore.isCollapsed ? undefined : t(translationKeys.pages.landingPage.navigationTitle)}
              icon={faHome}
              fullWidth
              tooltip={t(translationKeys.navigation.backToLandingPage)}
              tooltipPosition="right"
              tooltipDisabled={!sidebarStore.isCollapsed}
            />
          </Link>
        </div>

        <div className="sidebar-feature-menu">
          <FeatureList collapsed={sidebarStore.isCollapsed} />
        </div>

        {!!navigationStore.currentActiveMenuItem?.sidebarFeatureAreas && !isDelayedHidden && (
          <div className="sidebar-feature-content">
            {navigationStore.currentActiveMenuItem.sidebarFeatureAreas.map(config => (
              <SidebarFeatureAreaItem key={config.id} config={config} />
            ))}
          </div>
        )}

        {!!navigationStore.currentActiveMenuItem?.sidebarAddNewButton && (
          <div
            className="sidebar-new-button-container"
            onClick={() => sidebarStore.setIsAnonymous(false)}
            onKeyDown={e => e.key === "Enter" && sidebarStore.setIsAnonymous(false)}
            tabIndex={0}
            role="button"
          >
            {navigationStore.currentActiveMenuItem.sidebarAddNewButton()}
          </div>
        )}
      </div>
    </div>
  );
};

export const ContentSidebar = observer(RawContentSidebar);
